import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Alert,
  Paper,
  Typography,
} from "@mui/material";
import { Delete, UploadFile } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import { useCreateReviewMutation } from "../../services/review.service";

const DEFAULT_EXCLUSION_REASONS = [
  "Wrong population",
  "Wrong intervention",
  "Wrong comparator",
  "Wrong or no relevant outcome",
  "Wrong study design",
  "Duplicate or overlapping cohort",
  "Insufficient data",
  "Other",
];

const SOURCE_TYPES = [
  { value: "database", label: "Database / register" },
  { value: "other", label: "Other methods (e.g. citation searching)" },
];

const CreateReview = () => {
  const navigate = useNavigate();
  const [createReview, { isLoading }] = useCreateReviewMutation();

  const [title, setTitle] = useState("");
  const [question, setQuestion] = useState("");
  const [criteria, setCriteria] = useState("");
  const [exclusionReasons, setExclusionReasons] = useState(DEFAULT_EXCLUSION_REASONS);
  const [newReason, setNewReason] = useState("");

  const [sourceLabel, setSourceLabel] = useState("");
  const [sourceType, setSourceType] = useState("database");
  const [files, setFiles] = useState([]);

  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);

  const addReason = () => {
    if (!newReason.trim()) return;
    setExclusionReasons([...exclusionReasons, newReason.trim()]);
    setNewReason("");
  };

  const removeReason = (index) => {
    setExclusionReasons(exclusionReasons.filter((_, i) => i !== index));
  };

  const handleFileChange = (event) => {
    setFiles(Array.from(event.target.files || []));
  };

  const handleSubmit = async () => {
    setError("");
    setSummary(null);

    if (!title || !question || !criteria) {
      setError("Title, review question and screening criteria are required.");
      return;
    }
    if (!sourceLabel) {
      setError("A source label for the first import is required.");
      return;
    }
    if (files.length === 0) {
      setError("At least one import file (.nbib, .ris or .csv) is required.");
      return;
    }

    const formData = new FormData();
    formData.append("title", title);
    formData.append("question", question);
    formData.append("criteria", criteria);
    formData.append("exclusionReasons", JSON.stringify(exclusionReasons));
    formData.append("sourceLabel", sourceLabel);
    formData.append("sourceType", sourceType);
    files.forEach((file) => formData.append("files", file));

    try {
      const result = await createReview(formData).unwrap();
      setSummary(result.imports);
      setTimeout(() => navigate(`/reviews/${result.review._id}`), 1200);
    } catch (err) {
      setError(err?.data?.message || "Could not create the review.");
    }
  };

  return (
    <Box m="1.5rem 2.5rem">
      <Header title="New Systematic Review" subtitle="Set up screening criteria and the first import" />

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {summary && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Review created. Parsed {summary.reduce((s, i) => s + i.parsed, 0)} records across{" "}
          {summary.length} file(s) — {summary.reduce((s, i) => s + i.newCount, 0)} new,{" "}
          {summary.reduce((s, i) => s + i.duplicateOfExistingCount + i.duplicateWithinFileCount, 0)} duplicates removed,{" "}
          {summary.reduce((s, i) => s + i.possibleDuplicateCount, 0)} possible duplicates to check.
        </Alert>
      )}

      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography variant="h6" mb={2}>Review details</Typography>
        <TextField
          label="Title"
          fullWidth
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Review question"
          fullWidth
          multiline
          minRows={2}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Screening criteria (shown to reviewers on every card)"
          fullWidth
          multiline
          minRows={3}
          value={criteria}
          onChange={(e) => setCriteria(e.target.value)}
          sx={{ mb: 2 }}
        />

        <Typography variant="subtitle1" mb={1}>Full-text exclusion reasons</Typography>
        <List dense>
          {exclusionReasons.map((reason, index) => (
            <ListItem
              key={`${reason}-${index}`}
              secondaryAction={
                <IconButton edge="end" onClick={() => removeReason(index)}>
                  <Delete />
                </IconButton>
              }
            >
              <ListItemText primary={reason} />
            </ListItem>
          ))}
        </List>
        <Box display="flex" gap={1} mb={1}>
          <TextField
            label="Add a reason"
            size="small"
            fullWidth
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addReason()}
          />
          <Button variant="outlined" onClick={addReason}>Add</Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography variant="h6" mb={2}>First import</Typography>
        <TextField
          label="Source label (e.g. MEDLINE (PubMed), Embase, CENTRAL, Scopus, Citation searching)"
          fullWidth
          value={sourceLabel}
          onChange={(e) => setSourceLabel(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          select
          label="Source type"
          fullWidth
          value={sourceType}
          onChange={(e) => setSourceType(e.target.value)}
          sx={{ mb: 2 }}
        >
          {SOURCE_TYPES.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>
        <Button variant="outlined" component="label" startIcon={<UploadFile />}>
          Choose files (.nbib, .ris, .csv)
          <input type="file" hidden multiple accept=".nbib,.ris,.csv" onChange={handleFileChange} />
        </Button>
        {files.length > 0 && (
          <List dense>
            {files.map((file, index) => (
              <ListItem key={`${file.name}-${index}`}>
                <ListItemText primary={file.name} secondary={`${Math.round(file.size / 1024)} KB`} />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      <Button
        variant="contained"
        color="primary"
        sx={{ mt: 2 }}
        disabled={isLoading}
        onClick={handleSubmit}
      >
        {isLoading ? "Creating..." : "Create review"}
      </Button>
    </Box>
  );
};

export default CreateReview;
